import React, { useState, useEffect, useCallback } from "react";
import { TbSearch, TbPlus, TbX, TbChevronRight, TbChevronLeft, TbDatabase } from "react-icons/tb";
import { Button, Alert } from "../../components";
import { api } from "../../lib/api";
import "./Settings.css";

// Where a category's number actually comes from.
//
// A category is worthless if its data_source does not match the scan JSON exactly, and
// nobody remembers the shape of a 1.4 MB scan file. So the path is picked by walking a
// real sample rather than typed from memory — which is what the previous interface's
// "Data Selection Strategy" panel did, and this is that, rebuilt.
//
// A category can draw on more than one dataset ("Chain Dataset"), which is why this is a
// list of rows rather than a single field. The stored form is the one the rest of the
// platform already reads: `[Scan Type] path` per row, joined with ` + `.

const SCAN_TYPES = ["Network Scan", "Localhost Scan", "AD Scan", "MS365 Scan", "PII Scan"];

// The top-level key each scan type's records live under in the sample file. Without this
// the explorer opens at the root of the whole document and the first two clicks are the
// same two clicks every time.
const ROOT_KEY = {
  "Network Scan": "network_devices",
  "Localhost Scan": "localhost",
  "AD Scan": "Ad Scan",
  "MS365 Scan": "MS365",
  "PII Scan": "PII",
};

// One device in a scan does not carry every field: a machine with no WMI error has no
// wmi_error key at all. Showing only the first record would hide fields that exist on
// the others, so the array is flattened into a union of every key seen. It is a shape to
// pick from, not a record to read.
const mergeNodes = (nodes) => {
  if (!Array.isArray(nodes)) return nodes;
  const merged = {};
  nodes.forEach((node) => {
    if (node && typeof node === "object" && !Array.isArray(node)) {
      Object.keys(node).forEach((key) => {
        if (merged[key] === undefined) merged[key] = node[key];
        else if (merged[key] && node[key] && typeof merged[key] === "object" && typeof node[key] === "object") {
          merged[key] = mergeNodes([merged[key], node[key]]);
        }
      });
    }
  });
  return merged;
};

// Samples arrive either raw or wrapped in { data: {...} } depending on which export
// produced them.
const unwrap = (sample) => {
  const d = sample?.data;
  if (d && d.data && typeof d.data === "object" && !Array.isArray(d.data)) return d.data;
  return d;
};

export const serializeSources = (rows) =>
  rows.filter((r) => r.path.trim()).map((r) => `[${r.scan_type}] ${r.path.trim()}`).join(" + ");

// Reads back what serializeSources wrote, so an existing category opens with its rows
// intact rather than as one unparsed string.
export const parseSources = (value) => {
  if (!value) return [{ scan_type: SCAN_TYPES[0], path: "" }];
  const rows = value.split(" + ").map((chunk) => {
    const m = chunk.match(/^\[(.+?)\]\s*(.*)$/);
    return m
      ? { scan_type: SCAN_TYPES.includes(m[1]) ? m[1] : SCAN_TYPES[0], path: m[2] }
      : { scan_type: SCAN_TYPES[0], path: chunk.trim() };
  });
  return rows.length ? rows : [{ scan_type: SCAN_TYPES[0], path: "" }];
};

const DataSourcePicker = ({ rows, onChange }) => {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sampleError, setSampleError] = useState("");

  // Which row's explorer is open, and where it has walked to.
  const [openIndex, setOpenIndex] = useState(null);
  const [node, setNode] = useState(null);
  const [path, setPath] = useState([]);
  const [rootDepth, setRootDepth] = useState(0);

  // Two sources, tried in order.
  //
  // The shipped fixtures first, because they are small, stable and cover shapes a given
  // tenant may not have scanned yet. If they are unavailable — the endpoint reads them
  // off disk and answers with an empty list rather than an error when they are missing —
  // fall back to this tenant's own most recent scan, which is a better sample anyway:
  // it is the exact shape their scanner is producing today.
  useEffect(() => {
    let cancelled = false;

    const fromFixtures = async () => {
      const d = await api.get("s1/api/thresholds/samples");
      return d?.data || [];
    };

    const fromRealScan = async () => {
      const list = await api.post("s2/api/all_scan_results", {});
      const rows = (list?.result || []).filter((r) => r.file_path).slice(0, 5);
      for (const row of rows) {
        try {
          const read = await api.post("s3/api/read", { fileUrl: row.file_path });
          if (read?.view && typeof read.view === "object") {
            return [{
              id: `scan-${row.scan_id}`,
              name: `${row.organization_name || "Scan"} · ${String(row.created_at).slice(0, 10)}`,
              data: read.view,
            }];
          }
        } catch { /* try the next scan rather than giving up on the first bad file */ }
      }
      return [];
    };

    (async () => {
      let found = [];
      let why = "";
      try {
        found = await fromFixtures();
      } catch (e) {
        why = e.message || "The sample scans could not be read.";
      }
      if (!found.length) {
        try {
          found = await fromRealScan();
        } catch (e) {
          why = why || e.message || "No scan could be read to browse.";
        }
      }
      if (cancelled) return;
      setSamples(found);
      setSampleError(found.length ? "" : why);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, []);

  const setRow = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const addRow = () => onChange([...rows, { scan_type: rows[0]?.scan_type || SCAN_TYPES[0], path: "" }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  // Opening drops straight into the scan type's own records rather than at the document
  // root, and flattens the array so the fields of a single device are what you see.
  const openExplorer = useCallback((i) => {
    const sample = samples[0];
    if (!sample) { setNode(null); setPath([]); setRootDepth(0); setOpenIndex(i); return; }
    let data = unwrap(sample);
    const key = ROOT_KEY[rows[i].scan_type];
    let start = [];

    if (key && data && data[key] !== undefined) {
      let n = data[key];
      if (Array.isArray(n) && n.length) { start = [`${key}[*]`]; n = mergeNodes(n); }
      else start = [key];
      data = n;
    }
    setNode(data);
    setPath(start);
    setRootDepth(start.length);
    setOpenIndex(i);
  }, [rows, samples]);

  const drill = (key, value) => {
    if (Array.isArray(value)) {
      setPath((p) => [...p, `${key}[*]`]);
      setNode(mergeNodes(value));
    } else {
      setPath((p) => [...p, key]);
      setNode(value);
    }
  };

  // Walking back re-derives the node from the top rather than keeping a stack: the
  // merged view of an array is computed, so a cached parent would be a different object
  // from the one the same path produces now.
  const back = () => {
    const next = path.slice(0, -1);
    let data = unwrap(samples[0]);
    for (const seg of next) {
      const bare = seg.endsWith("[*]") ? seg.slice(0, -3) : seg;
      let v = data?.[bare];
      if (seg.endsWith("[*]") && Array.isArray(v)) v = mergeNodes(v);
      data = v;
    }
    setPath(next);
    setNode(data);
  };

  const select = (key) => {
    setRow(openIndex, { path: [...path, key].join(".") });
    setOpenIndex(null);
  };

  return (
    <div className="v2-ds">
      <div className="v2-ds-head">
        <span className="v2-ds-title"><TbDatabase size={14} /> Data selection strategy</span>
        <Button onClick={addRow}>
          <TbPlus size={13} style={{ verticalAlign: "-2px" }} /> Chain dataset
        </Button>
      </div>

      {sampleError && <Alert tone="warning">{sampleError}</Alert>}

      {rows.map((row, i) => (
        <div key={i} className="v2-ds-row">
          <div className="v2-ds-controls">
            <select className="v2-input v2-ds-type" value={row.scan_type}
                    aria-label={`Dataset ${i + 1} scan type`}
                    onChange={(e) => setRow(i, { scan_type: e.target.value })}>
              {SCAN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="v2-input v2-mono-input" value={row.path}
                   aria-label={`Dataset ${i + 1} path`}
                   placeholder="Path to discovered field"
                   onChange={(e) => setRow(i, { path: e.target.value })} />
            <Button variant="primary" disabled={loading}
                    onClick={() => (openIndex === i ? setOpenIndex(null) : openExplorer(i))}>
              <TbSearch size={14} style={{ verticalAlign: "-2px" }} /> Explore
            </Button>
            {rows.length > 1 && (
              <Button icon aria-label={`Remove dataset ${i + 1}`} onClick={() => removeRow(i)}>
                <TbX size={15} />
              </Button>
            )}
          </div>

          {openIndex === i && (
            <div className="v2-ds-explorer">
              <div className="v2-ds-explorer-head">
                <span>
                  Live schema · {row.scan_type}
                  {samples[0] && <span className="v2-dim"> · {samples[0].name}</span>}
                </span>
                <button type="button" className="v2-linkcell" onClick={() => setOpenIndex(null)}>Close</button>
              </div>

              <div className="v2-ds-breadcrumb">
                <span className="v2-dim">root</span>
                {path.map((p, idx) => (
                  <React.Fragment key={idx}>
                    <TbChevronRight size={11} className="v2-dim" />
                    <span className="v2-ds-crumb">{p}</span>
                  </React.Fragment>
                ))}
              </div>

              <div className="v2-ds-tree">
                {/* Never above the scan type's own root: going higher lands in parts of
                    the document that belong to a different scan type. */}
                {path.length > rootDepth && (
                  <button type="button" className="v2-linkcell v2-ds-up" onClick={back}>
                    <TbChevronLeft size={13} /> Up one level
                  </button>
                )}

                {!samples.length ? (
                  <div className="v2-ds-empty">
                    <b>No scan is available to browse.</b>
                    <p>
                      The shipped sample files could not be read, and no stored scan could be
                      opened either{sampleError ? ` — ${sampleError}` : ""}.
                    </p>
                    <p>Type the path by hand below, or try again once scans are readable.</p>
                  </div>
                ) : node && typeof node === "object" && Object.keys(node).length > 0 ? (
                  Object.keys(node).map((k) => {
                    const value = node[k];
                    const drillable = value && typeof value === "object";
                    return (
                      <div key={k} className="v2-ds-item">
                        <span className="v2-ds-key">{k}</span>
                        {!drillable && (
                          <span className="v2-ds-value">{JSON.stringify(value)}</span>
                        )}
                        {drillable ? (
                          <Button onClick={() => drill(k, value)}>Enter</Button>
                        ) : (
                          <Button variant="primary" onClick={() => select(k)}>Select</Button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="v2-hint" style={{ padding: ".5rem" }}>
                    Nothing further to open here. Step back up and pick a leaf field.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      <p className="v2-ghl-help">
        Stored as <code>[Scan Type] path</code>, joined with <code>+</code> when more than
        one dataset feeds the category.
      </p>
    </div>
  );
};

export default DataSourcePicker;
